import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function AdminReports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("today"); // today, week, month, year, custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      let params = {};
      if (period === "custom") {
        if (!startDate || !endDate) {
          setLoading(false);
          return; // need both dates
        }
        params = { startDate, endDate };
      } else {
        params = { period };
      }

      const res = await axiosInstance.get("/api/reports/admin/registration-stats", { params });
      
      // Transform data for recharts
      const rawLabels = res.data?.labels || [];
      const rawReg = res.data?.datasets?.registrations || [];
      const rawUpgrades = res.data?.datasets?.entrepreneurUpgrades || [];

      const chartData = rawLabels.map((lbl, idx) => ({
        name: lbl,
        Registrations: rawReg[idx] || 0,
        Upgrades: rawUpgrades[idx] || 0,
      }));

      setData({
        chart: chartData,
        summary: res.data?.summary || { totalRegistrations: 0, totalEntrepreneurUpgrades: 0 },
      });
    } catch (err) {
      setError("Failed to fetch reports. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSearch = () => {
    if (period !== "custom") setPeriod("custom");
    else fetchStats();
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.h1}>Registration Reports</div>
          <div style={styles.sub}>View registration and upgrade statistics over time</div>
        </div>
        <button style={styles.btn} onClick={() => navigate("/admin/dashboard")}>
          ← Dashboard
        </button>
      </div>

      <div style={styles.card}>
        {/* FILTERS */}
        <div style={styles.filtersWrapper}>
          <div style={styles.filterBtns}>
            {["today", "week", "month", "year"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{ ...styles.filterBtn, ...(period === p ? styles.activeFilter : {}) }}
              >
                {p.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => setPeriod("custom")}
              style={{ ...styles.filterBtn, ...(period === "custom" ? styles.activeFilter : {}) }}
            >
              CUSTOM
            </button>
          </div>

          {period === "custom" && (
            <div style={styles.customDateLine}>
              <input
                type="date"
                style={styles.dateInput}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span style={{ fontWeight: 900 }}>TO</span>
              <input
                type="date"
                style={styles.dateInput}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <button style={styles.searchBtn} onClick={handleCustomSearch}>
                Apply
              </button>
            </div>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.muted}>Loading statistics...</div>
        ) : !data ? (
          <div style={styles.muted}>No data loaded yet.</div>
        ) : (
          <>
            {/* SUMMARY WIDGETS */}
            <div style={styles.summaryGrid}>
              <div style={{ ...styles.summaryBox, background: "#f0f9ff", border: "1px solid #bae6fd" }}>
                <div style={styles.summaryLabel}>Total Registrations</div>
                <div style={{ ...styles.summaryValue, color: "#0ea5e9" }}>
                  {data.summary.totalRegistrations}
                </div>
              </div>
              <div style={{ ...styles.summaryBox, background: "#fdf4ff", border: "1px solid #fbcfe8" }}>
                <div style={styles.summaryLabel}>Total Upgrades (Entrepreneur)</div>
                <div style={{ ...styles.summaryValue, color: "#d946ef" }}>
                  {data.summary.totalEntrepreneurUpgrades}
                </div>
              </div>
            </div>

            {/* CHART */}
            <div style={styles.chartWrapper}>
              <h3 style={{ marginTop: 0, marginBottom: 20 }}>
                Performance Chart
                <span style={styles.dateRangeBadge}>
                  {data.summary.dateRange?.from || "-"} to {data.summary.dateRange?.to || "-"}
                </span>
              </h3>
              
              {data.chart.length === 0 ? (
                <div style={styles.muted}>No chart data available for this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={data.chart} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                    <Bar 
                      dataKey="Registrations" 
                      fill="#0ea5e9" 
                      radius={[4, 4, 0, 0]} 
                      barSize={40}
                    />
                    <Bar 
                      dataKey="Upgrades" 
                      fill="#d946ef" 
                      radius={[4, 4, 0, 0]} 
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: 20,
    minHeight: "100vh",
    background: "#f4f6f8",
    fontFamily: "Segoe UI, Inter, system-ui, sans-serif",
    color: "#111",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 10,
  },
  h1: { fontSize: 24, fontWeight: "bold" },
  sub: { fontSize: 14, color: "#666", marginTop: 4 },
  btn: {
    padding: "10px 16px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
  },
  card: {
    background: "#fff",
    padding: 24,
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid rgba(0,0,0,0.05)",
  },
  filtersWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    marginBottom: 24,
    paddingBottom: 24,
    borderBottom: "1px solid #edf2f7",
  },
  filterBtns: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  filterBtn: {
    padding: "8px 16px",
    borderRadius: 20,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: 13,
    color: "#475569",
    transition: "all 0.2s",
  },
  activeFilter: {
    background: "#111",
    color: "#fff",
    borderColor: "#111",
  },
  customDateLine: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    background: "#f8fafc",
    padding: "12px 16px",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },
  dateInput: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    outline: "none",
    fontWeight: "bold",
  },
  searchBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    background: "#0ea5e9",
    color: "#fff",
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
    marginBottom: 30,
  },
  summaryBox: {
    padding: 20,
    borderRadius: 12,
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#475569",
    marginBottom: 8,
  },
  summaryValue: {
    fontSize: 32,
    fontWeight: 900,
  },
  chartWrapper: {
    paddingTop: 10,
  },
  dateRangeBadge: {
    marginLeft: 12,
    fontSize: 12,
    padding: "4px 10px",
    background: "#f1f5f9",
    borderRadius: 20,
    fontWeight: "normal",
    color: "#64748b",
  },
  error: {
    padding: 12,
    background: "#fef2f2",
    color: "#b91c1c",
    borderRadius: 8,
    marginBottom: 16,
    fontWeight: "bold",
  },
  muted: {
    color: "#64748b",
    fontSize: 14,
    fontStyle: "italic",
  },
};
